import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState, } from 'react';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
// This needs to be 1px instead of rem because we don't want this value to scale.
const offset = '1px';
const defaultProps = {
    alwaysSticky: false,
    position: 'bottom',
};
const StickyContainer = forwardRef(({ alwaysSticky = defaultProps.alwaysSticky, children, className, containerRef, position = defaultProps.position, stickyBreakpoint, }, ref) => {
    const [isStuck, setIsStuck,] = useState();
    const stickyRef = useRef(null);
    const containerStyles = useMemo(() => (css `
        ${styleUtils.transition([
        'padding',
        'box-shadow',
    ])}
        ${alwaysSticky && css `
          position: fixed;
          width: 100%;
        `}
        ${!alwaysSticky && !stickyBreakpoint && css `
          position: sticky;
        `}
        ${!alwaysSticky && stickyBreakpoint && css `
          position: sticky;

          ${theme.media.greaterThan(stickyBreakpoint)(css `
            position: unset;
          `)}
        `}
        ${position === 'top' && css `
          top: -${offset};

          ${(isStuck || alwaysSticky) && styleUtils.bottomShadow}
        `}
        ${position === 'bottom' && css `
          bottom: -${offset};

          ${(isStuck || alwaysSticky) && styleUtils.topShadow}
        `}
        ${position === 'bottomOfFixedContainer' && css `
          bottom: 0;

          ${(isStuck) && styleUtils.topShadow}
        `}
      `), [
        alwaysSticky,
        isStuck,
        position,
        stickyBreakpoint,
    ]);
    useImperativeHandle(ref, () => stickyRef.current);
    useEffect(() => {
        var _a;
        if (alwaysSticky) {
            return;
        }
        const localContainerRef = (containerRef && containerRef.current
            ? containerRef.current
            : (_a = stickyRef.current) === null || _a === void 0 ? void 0 : _a.parentElement);
        let requestAnimationFrameId;
        if (position === 'top'
            || position === 'bottom') {
            const intersectionObserver = new IntersectionObserver(([entry,]) => {
                cancelAnimationFrame(requestAnimationFrameId);
                requestAnimationFrameId = (window
                    .requestAnimationFrame(() => {
                    var _a;
                    setIsStuck(!entry.isIntersecting && entry.intersectionRatio !== 0);
                    (_a = stickyRef.current) === null || _a === void 0 ? void 0 : _a.style.setProperty((position === 'top'
                        ? 'padding-top'
                        : 'padding-bottom'), entry.isIntersecting
                        ? '0'
                        : offset);
                }));
            }, {
                threshold: 1,
            });
            if (stickyRef.current) {
                intersectionObserver.observe(stickyRef.current);
            }
            return () => {
                intersectionObserver.disconnect();
            };
        }
        const callback = () => {
            cancelAnimationFrame(requestAnimationFrameId);
            requestAnimationFrameId = (window
                .requestAnimationFrame(() => {
                const scrollHeight = (localContainerRef === null || localContainerRef === void 0 ? void 0 : localContainerRef.scrollHeight) || 0;
                const scrollTop = (localContainerRef === null || localContainerRef === void 0 ? void 0 : localContainerRef.scrollTop) || 0;
                const clientHeight = (localContainerRef === null || localContainerRef === void 0 ? void 0 : localContainerRef.clientHeight) || 0;
                setIsStuck(scrollHeight !== (clientHeight + scrollTop));
            }));
        };
        const resizeObserver = (new ResizeObserver(callback));
        if (localContainerRef) {
            resizeObserver
                .observe(localContainerRef);
            localContainerRef
                .addEventListener('scroll', callback);
        }
        return () => {
            resizeObserver
                .disconnect();
            if (localContainerRef) {
                localContainerRef
                    .removeEventListener('scroll', callback);
            }
        };
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        alwaysSticky,
        containerRef === null || containerRef === void 0 ? void 0 : containerRef.current,
        position,
    ]);
    return (_jsx("div", Object.assign({ className: className, css: containerStyles, ref: stickyRef }, { children: children })));
});
StickyContainer.displayName = 'StickyContainer';
const MemoizedStickyContainer = memo(StickyContainer);
export { MemoizedStickyContainer as StickyContainer };
// MC-1068 Temporary hack for Storybook.
export default StickyContainer;
